/**
 * When the next carpool run is, in words.
 *
 * Shared because the staff app header and the admin dashboard must answer this
 * identically — and because both used to answer it themselves and disagreed:
 * the dashboard counted down to the *earliest* line of the day and then sat on
 * "Past scheduled start" once it passed, never advancing to the afternoon run.
 *
 * Works from the resolved schedule (`/api/carpool-schedule/active`) so it says
 * the same thing as the geofence and the admin schedule page.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export function parseTimeToMinutes(value) {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function formatClockTime(value) {
  const minutes = typeof value === 'number' ? value : parseTimeToMinutes(value);
  if (minutes === null) return null;

  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  const suffix = hours < 12 ? 'AM' : 'PM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${display}:${String(mins).padStart(2, '0')} ${suffix}`;
}

/** "in 25 minutes", "in 2 hours 10 minutes", "now". */
export function formatCountdown(msUntil) {
  if (!Number.isFinite(msUntil)) return null;
  if (msUntil <= 0) return 'now';

  const totalMinutes = Math.round(msUntil / 60000);
  if (totalMinutes < 60) return `in ${totalMinutes} minute${totalMinutes === 1 ? '' : 's'}`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalMinutes >= DAY_MS / 60000) {
    const days = Math.round(totalMinutes / (24 * 60));
    return `in ${days} day${days === 1 ? '' : 's'}`;
  }
  return minutes === 0
    ? `in ${hours} hour${hours === 1 ? '' : 's'}`
    : `in ${hours} hour${hours === 1 ? '' : 's'} ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * "Dismissal Time" on its own does not answer the question staff actually ask,
 * which is how long until the carpool starts.
 *
 * Works from the resolved schedule (`/api/carpool-schedule/active`) so it says
 * the same thing as the geofence and the admin schedule page.
 *
 * @returns {{label: string, detail: string|null, live: boolean}}
 */
export function describeNextCarpool(schedule, nowMinutes = null) {
  if (!schedule) return { label: '—', detail: null, live: false };

  if (schedule.no_school) {
    return { label: 'No dismissal today', detail: schedule.no_school_reason || null, live: false };
  }

  const lines = (schedule.lines || []).filter((line) => Number.isFinite(line.dismissal_minutes));
  if (!lines.length) {
    // Nothing is configured — say so plainly rather than a bare "Not set",
    // which reads like a loading failure.
    return { label: 'No dismissal time set', detail: 'Add a time in Pickup Zones or School Settings', live: false };
  }

  const current = Number.isFinite(nowMinutes) ? nowMinutes : schedule.school_minutes;
  const liveLine = lines.find((line) => line.live_now);
  if (liveLine) {
    return {
      label: 'Carpool running now',
      detail: `${liveLine.name} · ${formatClockTime(liveLine.dismissal_minutes)}`,
      lineName: liveLine.name || null,
      live: true,
    };
  }

  const upcoming = lines
    .filter((line) => line.dismissal_minutes >= current)
    .sort((left, right) => left.dismissal_minutes - right.dismissal_minutes)[0];

  if (!upcoming) {
    // Every line for today has already passed.
    const last = lines.sort((left, right) => right.dismissal_minutes - left.dismissal_minutes)[0];
    return { label: 'Done for today', detail: `Last was ${formatClockTime(last.dismissal_minutes)}`, live: false };
  }

  const minutesAway = upcoming.dismissal_minutes - current;
  return {
    label: formatClockTime(upcoming.dismissal_minutes),
    detail: minutesAway <= 0 ? 'starting now' : formatCountdown(minutesAway * 60000),
    // Which run is next matters once a school has more than one.
    lineName: upcoming.name || null,
    live: false,
  };
}
